import { Badge, Stack, Tabs, Text } from '@mantine/core';
import { IconBook2, IconBrain, IconFlask, IconHelpCircle, IconListCheck, IconPlugConnected } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import { showNotification } from '@mantine/notifications';

import { getKnowledgeLoopStatus, getKnowledgeUnitByFaq } from '@mygpt/app/apis/services/knowledgeLoop';
import { ZendeskSourcesList } from '@/components/Datasets/ZendeskSources/index';
import { DistillPanel } from './DistillPanel';
import { GapQueue } from './GapQueue';
import { Ledger } from './Ledger';
import { MemoryPanel } from './MemoryPanel';
import { ReviewQueue } from './ReviewQueue';
import { UnitDrawer } from './UnitDrawer';

type TabKey = 'distill' | 'queue' | 'gaps' | 'ledger' | 'memory' | 'sources';
const TABS: TabKey[] = ['ledger', 'queue', 'gaps', 'memory', 'sources', 'distill'];

/**
 * ナレッジベース詳細「ナレッジループ」タブ：
 * ナレッジ抽出（毎日の状態・設定・履歴）/ 審査キュー / ナレッジ不足 / ナレッジ台帳 / 学習ルール / 取込設定（Zendesk 接続）。
 * ?tab=queue&unit=<id> で運営画面（回答改善キュー）から特定の提案を直接開ける。
 */
export const KnowledgeLoop = () => {
  const { t } = useTranslation();
  const { datasetId } = useParams<{ datasetId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramTab = searchParams.get('tab') as TabKey | null;
  const [tab, setTab] = useState<TabKey>(paramTab && TABS.includes(paramTab) ? paramTab : 'ledger');
  const [deepUnit, setDeepUnit] = useState<string | null>(searchParams.get('unit'));
  const [queueCount, setQueueCount] = useState<number | null>(null);
  const [gapCount, setGapCount] = useState<number | null>(null);

  const refreshCount = useCallback(async () => {
    if (!datasetId) return;
    try {
      const { data } = await getKnowledgeLoopStatus(datasetId);
      setQueueCount(data.queue.total);
      setGapCount(data.open_gaps ?? null);
    } catch (e) {
      console.error('load knowledge loop status failed:', e);
    }
  }, [datasetId]);

  useEffect(() => {
    refreshCount();
  }, [refreshCount, tab]);

  // FAQ 画面からの直リンク（?faq=<id>）：その FAQ を投影している単元を開く。台帳にない手動 FAQ は知らせて終わり
  const paramFaq = searchParams.get('faq');
  useEffect(() => {
    if (!datasetId || !paramFaq) return;
    let alive = true;
    getKnowledgeUnitByFaq(datasetId, paramFaq)
      .then(({ data }) => alive && setDeepUnit(data.id))
      .catch(() => alive && showNotification({ color: 'yellow', message: t('knowledge_loop.faq_not_in_ledger') }))
      .finally(() => {
        setSearchParams(
          (prev) => {
            const p = new URLSearchParams(prev);
            p.delete('faq');
            return p;
          },
          { replace: true }
        );
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId, paramFaq]);

  const changeTab = (next: TabKey) => {
    setTab(next);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('tab', next);
        p.delete('unit');
        return p;
      },
      { replace: true }
    );
  };

  const closeDeepUnit = () => {
    setDeepUnit(null);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('unit');
        return p;
      },
      { replace: true }
    );
  };

  if (!datasetId) return null;

  const countBadge = (n: number | null, color: string) =>
    n ? (
      <Badge size="xs" variant="filled" color={color} circle={n < 10}>
        {n}
      </Badge>
    ) : undefined;

  return (
    <section className="flex flex-col relative h-full">
      <Stack gap={2} mb="sm">
        <Text fw={600}>{t('knowledge_loop.title')}</Text>
        <Text size="xs" c="gray.6">
          {t('knowledge_loop.description')}
        </Text>
      </Stack>
      <Tabs value={tab} onChange={(v) => v && changeTab(v as TabKey)} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="ledger" leftSection={<IconBook2 size={14} />}>
            {t('knowledge_loop.tab.ledger')}
          </Tabs.Tab>
          <Tabs.Tab
            value="queue"
            leftSection={<IconListCheck size={14} />}
            rightSection={countBadge(queueCount, 'yellow')}
          >
            {t('knowledge_loop.tab.queue')}
          </Tabs.Tab>
          <Tabs.Tab
            value="gaps"
            leftSection={<IconHelpCircle size={14} />}
            rightSection={countBadge(gapCount, 'orange')}
          >
            {t('knowledge_loop.tab.gaps')}
          </Tabs.Tab>
          <Tabs.Tab value="memory" leftSection={<IconBrain size={14} />}>
            {t('knowledge_loop.tab.memory')}
          </Tabs.Tab>
          <Tabs.Tab value="sources" leftSection={<IconPlugConnected size={14} />}>
            {t('knowledge_loop.tab.sources')}
          </Tabs.Tab>
          <Tabs.Tab value="distill" leftSection={<IconFlask size={14} />}>
            {t('knowledge_loop.tab.distill')}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="distill">
          <DistillPanel
            datasetId={datasetId}
            onOpenQueue={() => changeTab('queue')}
            onOpenSources={() => changeTab('sources')}
          />
        </Tabs.Panel>
        <Tabs.Panel value="queue">
          <ReviewQueue datasetId={datasetId} onChanged={refreshCount} />
        </Tabs.Panel>
        <Tabs.Panel value="gaps">
          <GapQueue datasetId={datasetId} onChanged={refreshCount} />
        </Tabs.Panel>
        <Tabs.Panel value="ledger">
          <Ledger datasetId={datasetId} />
        </Tabs.Panel>
        <Tabs.Panel value="memory">
          <MemoryPanel datasetId={datasetId} />
        </Tabs.Panel>
        <Tabs.Panel value="sources">
          <ZendeskSourcesList />
        </Tabs.Panel>
      </Tabs>

      {/* 直リンク（?unit=）で開く審査ドロワー。タブの一覧とは独立に開く */}
      <UnitDrawer datasetId={datasetId} unitId={deepUnit} onClose={closeDeepUnit} onReviewed={refreshCount} />
    </section>
  );
};
